import React from 'react';
import { Play, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { resolveMediaUrl, formatDate, formatTime } from '@/utils/helpers';

export const GameCard = ({ game, onWatch }) => {
  // New stream_provider model — preferred
  const provider = game.stream_provider;
  const streamUrl = game.stream_url;
  const EMBEDDED = new Set(['youtube', 'facebook', 'twitch']);
  const hasNewStream = !!(provider && streamUrl);
  const hasLegacyStream = !!(game.video_sources && game.video_sources.length > 0);

  // Legacy fields (graphic generators / "Plus" badge)
  const hasFreeSources = game.video_sources?.some(vs => vs.is_free);
  const hasPlusContent = game.video_sources?.some(vs => vs.type === 'uscreen');
  const primarySource = game.video_sources?.[0];

  const handleClick = () => {
    // If new stream model is set, route accordingly
    if (hasNewStream) {
      if (EMBEDDED.has(provider)) {
        onWatch(game);
      } else {
        // Hudl / Uscreen / VEO / Other → external redirect, no modal
        window.open(streamUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    // Legacy fallback
    if (primarySource?.type === 'uscreen' && !primarySource?.is_free) {
      window.open(primarySource.url, '_blank');
    } else {
      onWatch(game);
    }
  };

  return (
    <div 
      className="game-card group cursor-pointer"
      onClick={handleClick}
      data-testid={`game-card-${game.id}`}
    >
      <div className="relative aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-t-xl overflow-hidden">
        {game.thumbnail ? (
          <img src={resolveMediaUrl(game.thumbnail)} alt={`${game.home_team_name} vs ${game.away_team_name}`} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl mb-2">VS</div>
              <div className="text-sm text-gray-400">{game.league_name}</div>
            </div>
          </div>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="text-center">
            <Play className="w-12 h-12 text-white mx-auto mb-2" />
            <span className="text-white font-medium">
              {primarySource?.type === 'uscreen' && !primarySource?.is_free ? 'Watch on Plus' : 'Watch Now'}
            </span>
          </div>
        </div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {game.status === 'live' && (
            <Badge className="bg-red-500 text-white animate-pulse">LIVE</Badge>
          )}
          {hasFreeSources && (
            <Badge className="bg-green-500 text-white">FREE</Badge>
          )}
          {hasPlusContent && !hasFreeSources && (
            <Badge className="bg-wpsl-blue text-white">PLUS</Badge>
          )}
          {hasNewStream && !EMBEDDED.has(provider) && (
            <Badge className="bg-wpsl-red text-white">EXTERNAL</Badge>
          )}
        </div>

        {/* Score (if live or finished) */}
        {(game.status === 'live' || game.status === 'finished') && game.home_score !== null && (
          <div className="absolute bottom-3 right-3 bg-black/80 px-3 py-1 rounded-lg">
            <span className="text-white font-bold">{game.home_score} - {game.away_score}</span>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="text-sm text-wpsl-blue mb-2">{game.league_name}</div>
        <h3 className="text-white font-semibold mb-2">
          {game.home_team_name || 'TBD'} vs {game.away_team_name || 'TBD'}
        </h3>
        <div className="flex items-center text-gray-400 text-sm">
          <Clock className="w-4 h-4 mr-1" />
          {formatDate(game.scheduled_time)} at {formatTime(game.scheduled_time)}
        </div>
        {game.production_company_name && (
          <div
            className="mt-3 pt-3 border-t border-wpsl-blue/15 flex items-center gap-2"
            data-testid={`game-card-production-${game.id}`}
          >
            {game.production_company_logo && (
              <img
                src={resolveMediaUrl(game.production_company_logo)}
                alt={game.production_company_name}
                className="h-5 w-auto opacity-80"
              />
            )}
            <div className="text-[11px] uppercase tracking-wider text-gray-500">
              Produced by{' '}
              <span className="text-wpsl-blue font-semibold normal-case tracking-normal">
                {game.production_company_name}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameCard;
